import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { z } from "zod";
import {
  type PhoneCommandRequest,
  type PhoneOutcome,
  PhoneOutcomeSchema,
  type PhoneSummary,
  PhoneSummarySchema,
} from "../src/phone-call-view.ts";

const ViewSchema = z.object({
  outcome: PhoneOutcomeSchema.nullable(),
  summary: PhoneSummarySchema.nullable(),
  planRevision: z.number(),
  replyOutstanding: z.boolean(),
  handoff: z.object({ name: z.string(), contact_id: z.string() }).nullable(),
  history: z.array(
    z.object({ revision: z.number(), actorId: z.string().nullable(), reason: z.string() }),
  ),
});
const LABEL = {
  collecting: "Collecting call details",
  ambiguous: "Call details need clarification",
  ready: "Ready to call",
  withdrawn: "Call agreement withdrawn",
  failed: "Call assessment failed",
  stale: "Call details awaiting reassessment",
};
type View = z.infer<typeof ViewSchema>;
type Command = PhoneCommandRequest["command"];
type Assessment = PhoneOutcome["assessment"];
type Evidence = { sourceId: string; quote: string };
type PendingRequest = { key: string; request: PhoneCommandRequest };
type Send = (command: Command) => Promise<void>;

async function loadPhoneView(planApi: string): Promise<View> {
  const response = await fetch(`${planApi}/phone`, { credentials: "same-origin" });
  if (!response.ok)
    throw new Error(
      response.status === 401
        ? "Sign in again to read call details."
        : "Call details could not be loaded.",
    );
  return ViewSchema.parse(await response.json());
}

/** A retry of the same command keeps its operation ID, so a lost response cannot apply twice. */
function pendingPhoneRequest(
  pending: PendingRequest | null,
  command: Command,
  expectedRevision: number,
  expectedPlanRevision: number,
): PendingRequest {
  const key = JSON.stringify(command);
  if (pending?.key === key) return pending;
  return {
    key,
    request: { operationId: crypto.randomUUID(), expectedRevision, expectedPlanRevision, command },
  };
}

function phoneSaveFailure(status: number): string {
  if (status === 409)
    return "The details changed or this action is no longer available. Your input is preserved. Review the latest details before saving again.";
  return status === 401 || status === 403
    ? "You do not have permission to change these details."
    : "Could not save. Check your connection and review the latest history before trying again.";
}

async function copyCallDetails(assessment: Assessment): Promise<string> {
  try {
    await navigator.clipboard.writeText(
      `${assessment.contact?.name ?? ""}\n${assessment.number?.raw ?? ""}\n${assessment.restrictions.map((item) => item.text).join("\n")}`,
    );
    return "Call details copied. Handling is still recorded separately.";
  } catch {
    return "Copy failed. Select and copy the details above.";
  }
}

function useCorrectionDraft() {
  const [contactName, setContactName] = useState("");
  const [agreement, setAgreement] = useState<"absent" | "direct" | "reported">("absent");
  const [restrictions, setRestrictions] = useState("");
  const [restrictionKind, setRestrictionKind] = useState<"timing" | "caller" | "channel">("timing");
  const [number, setNumber] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [quote, setQuote] = useState("");
  const [reason, setReason] = useState("");
  /** Opening the form starts from the current details; the evidence fields keep what was typed. */
  const seed = (assessment: Assessment) => {
    setContactName(assessment.contact?.name ?? "");
    setNumber(assessment.number?.raw ?? "");
    setAgreement(
      assessment.agreement.state === "agreed" ? (assessment.agreement.basis ?? "absent") : "absent",
    );
    setRestrictions(assessment.restrictions.map((item) => item.text).join("\n"));
  };
  return {
    contactName,
    setContactName,
    agreement,
    setAgreement,
    restrictions,
    setRestrictions,
    restrictionKind,
    setRestrictionKind,
    number,
    setNumber,
    sourceId,
    setSourceId,
    quote,
    setQuote,
    reason,
    setReason,
    seed,
  };
}
type CorrectionDraft = ReturnType<typeof useCorrectionDraft>;

function detailsCorrection(draft: CorrectionDraft, assessment: Assessment): Command | null {
  const { sourceId, quote, reason, agreement, number, restrictionKind } = draft;
  const source = assessment.sources.find((item) => item.id === sourceId);
  if (!source) return null;
  const evidence = { sourceId, quote };
  const id = agreement === "reported" ? `referral:${sourceId}` : source.authorId;
  return {
    type: "correct_details",
    sourceId,
    quote,
    reason,
    proposal: {
      contact: {
        id,
        name: draft.contactName,
        authorId: agreement === "reported" ? null : source.authorId,
        evidence,
      },
      agreement: {
        state: agreement === "absent" ? "absent" : "agreed",
        basis: agreement === "absent" ? null : agreement,
        evidence: agreement === "absent" ? null : evidence,
      },
      numbers: number
        ? [
            {
              raw: number,
              contactId: id,
              evidence,
              source: source.kind,
              preference: "correction",
              kind: "phone",
            },
          ]
        : [],
      restrictions: draft.restrictions
        .split("\n")
        .map((text) => text.trim())
        .filter(Boolean)
        .map((text) => ({ kind: restrictionKind, text, evidence })),
      ambiguities: [],
    },
  };
}

function PhoneNotices({
  state,
  outcome,
  replyOutstanding,
}: {
  state: PhoneSummary["state"];
  outcome: PhoneOutcome;
  replyOutstanding: boolean;
}) {
  return (
    <>
      {state === "stale" && (
        <p role="alert">
          These details may be obsolete. Wait for the new assessment before using or handing them
          over.
        </p>
      )}
      {outcome.changedAfterHandling && (
        <p role="alert">Previously handled details changed. Notify anyone who received them.</p>
      )}
      {outcome.correctedAt && replyOutstanding && (
        <p>Phone details were corrected. Update any invalidated reply before approving it.</p>
      )}
    </>
  );
}

function agreementLabel(agreement: Assessment["agreement"]): string {
  if (agreement.state !== "agreed") return "Not agreed";
  return agreement.basis === "reported" ? "Agreement reported by referrer" : "Direct agreement";
}

function PhoneDetails({ outcome }: { outcome: PhoneOutcome }) {
  const { assessment, handling } = outcome;
  return (
    <>
      <dl>
        <dt>Contact</dt>
        <dd>{assessment.contact?.name ?? "Not confirmed"}</dd>
        <dt>Number</dt>
        <dd>
          {assessment.number?.raw ?? "Not collected"}
          {assessment.number && !assessment.number.international && (
            <small> · Country code not confirmed</small>
          )}
        </dd>
        <dt>Agreement</dt>
        <dd>{agreementLabel(assessment.agreement)}</dd>
        <dt>Handling</dt>
        <dd>{handling ? `Handled by ${handling.actorId} at ${handling.at}` : "Outstanding"}</dd>
      </dl>
      {assessment.restrictions.length > 0 && (
        <ul>
          {assessment.restrictions.map((item) => (
            <li key={`${item.kind}:${item.text}`}>{item.text}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function HandlingActions({
  data,
  outcome,
  state,
  disabled,
  send,
}: {
  data: View;
  outcome: PhoneOutcome;
  state: PhoneSummary["state"];
  disabled: boolean;
  send: Send;
}) {
  if (outcome.handling)
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => void send({ type: "undo_handling", reason: "Rep undid manual handling" })}
      >
        Undo handling
      </button>
    );
  if (state !== "ready" && !outcome.changedAfterHandling) return null;
  const callerRestricted = outcome.assessment.restrictions.some((item) => item.kind === "caller");
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => void send({ type: "handle", method: "manual" })}
      >
        {outcome.changedAfterHandling
          ? "Mark correction handled manually"
          : "Mark handled manually"}
      </button>
      {state === "ready" && data.handoff && (
        <button
          type="button"
          disabled={disabled || callerRestricted}
          onClick={() => void send({ type: "handle", method: "handoff" })}
        >
          Mark handed over to {data.handoff.name}
        </button>
      )}
      {data.handoff && callerRestricted && (
        <p>Caller restrictions require manual handling; the configured handoff may not match.</p>
      )}
    </>
  );
}

function DetailsCorrection({
  draft,
  assessment,
  send,
}: {
  draft: CorrectionDraft;
  assessment: Assessment;
  send: Send;
}) {
  const { sourceId, quote, reason } = draft;
  return (
    <details>
      <summary>Correct contact, agreement or restrictions</summary>
      <p>
        Choose the source above and quote the words supporting the contact, agreement, number and
        restrictions. This replaces the current details. A number alone never proves agreement.
      </p>
      <label>
        Contact name
        <input
          value={draft.contactName}
          onChange={(event) => draft.setContactName(event.target.value)}
        />
      </label>
      <label>
        Agreement
        <select
          value={draft.agreement}
          onChange={(event) =>
            draft.setAgreement(z.enum(["absent", "direct", "reported"]).parse(event.target.value))
          }
        >
          <option value="absent">Not agreed</option>
          <option value="direct">The writer explicitly agreed</option>
          <option value="reported">The writer reports this contact agreed</option>
        </select>
      </label>
      <label>
        Restriction type
        <select
          value={draft.restrictionKind}
          onChange={(event) =>
            draft.setRestrictionKind(
              z.enum(["timing", "caller", "channel"]).parse(event.target.value),
            )
          }
        >
          <option value="timing">Timing</option>
          <option value="caller">Caller</option>
          <option value="channel">Channel</option>
        </select>
      </label>
      <label>
        Restrictions, one per line
        <textarea
          value={draft.restrictions}
          onChange={(event) => draft.setRestrictions(event.target.value)}
        />
      </label>
      <button
        type="button"
        disabled={!draft.contactName || !sourceId || !quote || !reason}
        onClick={() => {
          const command = detailsCorrection(draft, assessment);
          if (command) void send(command);
        }}
      >
        Save evidenced details
      </button>
    </details>
  );
}

function CorrectionForm({
  draft,
  assessment,
  disabled,
  send,
}: {
  draft: CorrectionDraft;
  assessment: Assessment;
  disabled: boolean;
  send: Send;
}) {
  const { number, sourceId, quote, reason } = draft;
  return (
    <fieldset disabled={disabled}>
      <legend>Evidenced correction</legend>
      <label>
        Number
        <input value={number} onChange={(event) => draft.setNumber(event.target.value)} />
      </label>
      <label>
        Source
        <select value={sourceId} onChange={(event) => draft.setSourceId(event.target.value)}>
          <option value="">Choose conversation source</option>
          {assessment.sources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.messageId} · {source.kind}
            </option>
          ))}
        </select>
      </label>
      <label>
        Exact supporting words
        <textarea value={quote} onChange={(event) => draft.setQuote(event.target.value)} />
      </label>
      <label>
        Reason
        <textarea value={reason} onChange={(event) => draft.setReason(event.target.value)} />
      </label>
      <p>
        Entering a number cannot establish agreement. Changed details invalidate queued reply
        approval; review and redraft the reply.
      </p>
      <button
        type="button"
        disabled={!number || !sourceId || !quote || !reason}
        onClick={() => void send({ type: "correct_number", number, sourceId, quote, reason })}
      >
        Save correction
      </button>
      <DetailsCorrection draft={draft} assessment={assessment} send={send} />
      <button
        type="button"
        disabled={!reason || !sourceId || !quote}
        onClick={() => void send({ type: "withdraw", reason, sourceId, quote })}
      >
        Withdraw call readiness
      </button>
    </fieldset>
  );
}

/** Each distinct quote once, in the order agreement, number, correction. */
function distinctEvidence(outcome: PhoneOutcome): Evidence[] {
  return [
    outcome.assessment.agreement.evidence,
    outcome.assessment.number?.evidence,
    outcome.correctionEvidence,
  ]
    .filter((item) => item != null)
    .filter(
      (item, index, items) =>
        items.findIndex(
          (candidate) => candidate.sourceId === item.sourceId && candidate.quote === item.quote,
        ) === index,
    );
}

function sourceMessageHref(messageId: string): string {
  return `#message-${encodeURIComponent(messageId.startsWith("bison:") ? messageId : `bison:${messageId}`)}`;
}

function EvidenceHistory({
  outcome,
  history,
}: {
  outcome: PhoneOutcome;
  history: View["history"];
}) {
  return (
    <details>
      <summary>Evidence and correction history</summary>
      {distinctEvidence(outcome).map((item) => {
        const source = outcome.assessment.sources.find(
          (candidate) => candidate.id === item.sourceId,
        );
        return (
          <p key={`${item.sourceId}:${item.quote}`}>
            <q>{item.quote}</q>{" "}
            {source && <a href={sourceMessageHref(source.messageId)}>Open source message</a>}
          </p>
        );
      })}
      <ol>
        {history.map((revision) => (
          <li key={revision.revision}>
            Revision {revision.revision} · {revision.actorId ?? "Assessment"} · {revision.reason}
          </li>
        ))}
      </ol>
    </details>
  );
}

export function PhoneCallCard({
  planApi,
  planRevision,
}: {
  planApi: string;
  planRevision: number;
}) {
  const cache = useQueryClient();
  const view = useQuery({
    queryKey: ["phone", planApi, planRevision],
    queryFn: () => loadPhoneView(planApi),
    refetchInterval: 15000,
  });
  const correctionButton = useRef<HTMLButtonElement>(null);
  const pendingRequest = useRef<PendingRequest | null>(null);
  const draft = useCorrectionDraft();
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const data = view.data;
  const send = async (command: Command) => {
    if (!data?.outcome || busy) return;
    setBusy(true);
    setFeedback("");
    try {
      const pending = pendingPhoneRequest(
        pendingRequest.current,
        command,
        data.outcome.revision,
        data.planRevision,
      );
      pendingRequest.current = pending;
      const response = await fetch(`${planApi}/phone`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(pending.request),
      });
      if (response.status === 409) {
        pendingRequest.current = null;
        await view.refetch();
      }
      if (!response.ok) throw new Error(phoneSaveFailure(response.status));
      pendingRequest.current = null;
      setEditing(false);
      correctionButton.current?.focus();
      setFeedback("Call details saved.");
      await Promise.all([
        view.refetch(),
        cache.invalidateQueries({ queryKey: ["plans"] }),
        cache.invalidateQueries({ queryKey: ["plan"] }),
      ]);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Could not save call details.");
    } finally {
      setBusy(false);
    }
  };
  if (view.isError)
    return (
      <p role="alert">
        {view.error.message}{" "}
        <button type="button" onClick={() => void view.refetch()}>
          Retry
        </button>
      </p>
    );
  if (!data) return <p role="status">Loading call details…</p>;
  if (!data.outcome || !data.summary) return <p>Call details have not been assessed.</p>;
  const { outcome, summary } = data;
  const assessment = outcome.assessment;
  const disabled = busy || summary.state === "stale";
  return (
    <section aria-label="Phone call details" className="lcr-phone-card">
      <h3>{LABEL[summary.state]}</h3>
      <PhoneNotices
        state={summary.state}
        outcome={outcome}
        replyOutstanding={data.replyOutstanding}
      />
      <PhoneDetails outcome={outcome} />
      <p>
        Readiness does not mean call immediately. Handling does not send an email or confirm that a
        call occurred.
      </p>
      {assessment.number && (
        <button
          type="button"
          disabled={disabled}
          onClick={async () => setFeedback(await copyCallDetails(assessment))}
        >
          Copy call details
        </button>
      )}
      <HandlingActions
        data={data}
        outcome={outcome}
        state={summary.state}
        disabled={disabled}
        send={send}
      />
      <button
        type="button"
        disabled={disabled}
        ref={correctionButton}
        aria-expanded={editing}
        onClick={() => {
          if (!editing) draft.seed(assessment);
          setEditing(!editing);
        }}
      >
        Correct call details
      </button>
      {editing && (
        <CorrectionForm draft={draft} assessment={assessment} disabled={disabled} send={send} />
      )}
      <EvidenceHistory outcome={outcome} history={data.history} />
      <p role="status" aria-live="polite">
        {feedback}
      </p>
    </section>
  );
}
